import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useLeafletContext } from '@react-leaflet/core'
import { useMap } from 'react-leaflet'
import parseGeoraster from 'georaster'
import GeoRasterLayer from 'georaster-layer-for-leaflet'

function getColor(value, iniciosIntervalos, colores) {
    if (value <= iniciosIntervalos[0]) return colores[0]
    if (value >= iniciosIntervalos[iniciosIntervalos.length - 1])
        return colores[colores.length - 1]

    // Buscar el intervalo en el que cae el valor
    for (let i = 0; i < iniciosIntervalos.length - 1; i++) {
        if (value >= iniciosIntervalos[i] && value < iniciosIntervalos[i + 1]) {
            // Interpolación lineal entre los dos colores del intervalo
            let t =
                (value - iniciosIntervalos[i]) /
                (iniciosIntervalos[i + 1] - iniciosIntervalos[i])
            return interpolateColor(colores[i], colores[i + 1], t)
        }
    }
}

// Función para interpolar entre dos colores hexadecimales
function interpolateColor(color1, color2, t) {
    const c1 = hexToRgb(color1)
    const c2 = hexToRgb(color2)

    const r = Math.round(c1.r + t * (c2.r - c1.r))
    const g = Math.round(c1.g + t * (c2.g - c1.g))
    const b = Math.round(c1.b + t * (c2.b - c1.b))

    return rgbToHex(r, g, b)
}

// Función para convertir un color hexadecimal a RGB
function hexToRgb(hex) {
    const bigint = parseInt(hex.slice(1), 16)
    return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
    }
}

// Función para convertir un color RGB a hexadecimal
function rgbToHex(r, g, b) {
    return (
        '#' +
        ((1 << 24) + (r << 16) + (g << 8) + b)
            .toString(16)
            .slice(1)
            .toUpperCase()
    )
}

export function TifLayer({
    url,
    escala,
    opacity,
    escalaContinua,
    active,
    onReady,
}) {
    if (!active) return
    const geoTiffLayerRef = useRef()
    const context = useLeafletContext()
    const map = useMap()

    useEffect(() => {
        const container = context.layerContainer || context.map
        fetch(url, {
            mode: 'no-cors',
        })
            .then((response) => {
                if (response.ok) {
                    return response.arrayBuffer()
                }
                throw new Error('Archivo no encontrado: ' + url)
            })

            .then((arrayBuffer) => {
                parseGeoraster(arrayBuffer).then((georaster) => {
                    /* 
                    const min = georaster.mins[0]
                    const range = georaster.ranges[0]
                    const scale = chroma.scale('Spectral').domain([1, 0])
                */
                    const options = {
                        pixelValuesToColorFn: function (pixelValues) {
                            const pixelValue = pixelValues[0]
                            if (Number.isNaN(pixelValue)) return null
                            //const scaledPixelValue = (pixelValue - min) / range
                            //const color = scale(scaledPixelValue).hex()
                            const colCon = escalaContinua
                                ? getColor(
                                      pixelValue,
                                      escala.escalaValores,
                                      escala.escalaColor
                                  )
                                : escala.getColCon(pixelValue).color

                            return colCon
                        },
                        noDataValue: NaN,
                        //mask: urlGeoJsonMask,
                        resolution: 512,
                        opacity,
                        georaster: undefined,
                    }
                    options.georaster = georaster
                    //if (geoTiffLayerRef.current) return // Prevent duplicate loading of layers
                    geoTiffLayerRef.current = new GeoRasterLayer(options)

                    container.addLayer(geoTiffLayerRef.current)
                    map.fitBounds(geoTiffLayerRef.current.getBounds())
                    onReady()
                })
            })
            .catch((err) => {
                console.log(err)
            })
        return () => {
            if (geoTiffLayerRef.current && map) {
                map.removeLayer(geoTiffLayerRef.current)
            }
        }
    }, [url])

    return null
}

TifLayer.propTypes = {
    url: PropTypes.string,
    escala: PropTypes.object,
    opacity: PropTypes.number,
    escalaContinua: PropTypes.bool,
    active: PropTypes.bool,
    onReady: PropTypes.func,
}
